import {
  Box,
  Typography,
  FormControl,
  Stack,
  Grid,
  Checkbox,
  styled,
  TextField,
} from "@mui/material";
import { useCallback, useContext, useEffect, useState } from "react";
import "./ServicesInvoiceSimulator.scss";
import { useTranslation } from "react-i18next";
import TotalEnergiesContext from "../../core/store/context/TotalEnergiesContext";
import {
  ManagementRequestInfo,
  PostCallCenterRequestBody,
  RequestStateDescriptionData,
} from "../../core/models/managements/managements.model";
import ButtonElement from "../UI/ButtonElement/ButtonElement";
import { OwnerDataCups } from "../../core/models/ownerData/ownerData.model";
import ServicesHeader from "../ServicesUpdate/ServicesUpdateHeader/ServicesUpdateHeader";
import ConsumptionsManagementsDeliveryAddressComponent from "../ConsumptionsManagementsDeliveryAddressComponent/ConsumptionsManagementsDeliveryAddressComponent";
import { initialOwnerDataCup } from "../../utils/ownerData/ownerData.util";
import ManagementsRequestState from "../Managements/ManagementsRequestState/ManagementsRequestState";
import {
  getManagementsRequestTypeData,
  initialManagementRequestInfo,
} from "../../utils/components/managements/managements.util";
import {
  MANAGEMENTS_STATE_ERROR,
  MANAGEMENTS_STATE_PENDING,
  SERVICES_SUBTYPE_OPTIMIZE_BILLS,
} from "../../core/constants/constants";
import { postCallCenter as postCallCenterFetch } from "../../core/services/managements/managements.service";
import { formatDate } from "../../utils/dateUtils/formatDate.util";
import theme from "../../muiTheme";
import {
  getRecipientId,
  getServiceSubTypeId,
  getServiceTypeId,
} from "../../utils/components/services/services.util";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";

const ServicesInvoiceSimulator = () => {
  const { t } = useTranslation();
  const { ownerData, userData } = useContext(TotalEnergiesContext);
  const [isConsentAccepted, setIsConsentAccepted] = useState<boolean>(false);
  const [selectedDeliveryAddress, setSelectedDeliveryAddress] =
    useState<OwnerDataCups>(initialOwnerDataCup);
  const [managementRequestInfo, setManagementRequestInfo] =
    useState<ManagementRequestInfo>(initialManagementRequestInfo);
  const [step, setStep] = useState<number>(0);
  const [isClaimLoading, setIsClaimLoading] = useState<boolean>(false);
  const [requestState, setRequestState] = useState<string>("");
  const [requestStateData, setRequestStateData] =
    useState<RequestStateDescriptionData>({
      title: "",
      description: "",
      descriptionExtra: "",
    });
  const [dateNextInvoice, setDateNextInvoice] = useState<any>();
  const [isButtonDisabled, setIsButtonDisabled] = useState(true);

  const checkButtonDisabled = useCallback(() => {
    if (selectedDeliveryAddress.idCups && isConsentAccepted) {
      setIsButtonDisabled(false);
      return;
    }
    setIsButtonDisabled(true);
  }, [selectedDeliveryAddress, isConsentAccepted]);

  useEffect(() => {
    checkButtonDisabled();
  }, [checkButtonDisabled]);

  const handleSendRequest = () => {
    if (ownerData && userData) {
      const message = `El usuario ha realizado una petición de simulación de factura. Próxima factura: ${dateNextInvoice?.$d.toLocaleDateString(
        "es",
        {
          year: "numeric",
          month: "long",
        }
      )}`;
      postCallCenter(message, selectedDeliveryAddress);
    }
  };

  const postCallCenter = (
    incidenceText: string,
    ownerDataCupSelected: OwnerDataCups
  ): void => {
    const { contractCode, envirorment, idContract } = ownerDataCupSelected;

    if (userData) {
      const idRegisterType = getServiceTypeId(envirorment);
      const IdSubTipoReclamacion = getServiceSubTypeId(
        idRegisterType,
        SERVICES_SUBTYPE_OPTIMIZE_BILLS
      )
      const requestBody: PostCallCenterRequestBody = {
        TextoIncidencia: incidenceText,
        CodigosContratos: [+contractCode],
        IdsContratos: [+idContract],
        IdTipoRegistro: idRegisterType,
        IdSubTipoReclamacion,
        idUsuarioDestino: getRecipientId(idRegisterType, IdSubTipoReclamacion),
      };
      setIsClaimLoading(true);
      setStep(1);

      postCallCenterFetch(requestBody, userData.token)
        .then(([{ IdCallCenter, IdCarpetaIncidencias }]) => ({
          IdCallCenter,
          IdCarpetaIncidencias,
        }))
        .then(async ({ IdCallCenter, IdCarpetaIncidencias }) => {
          setRequestState(MANAGEMENTS_STATE_PENDING);
          setRequestStateData(
            getManagementsRequestTypeData(
              SERVICES_SUBTYPE_OPTIMIZE_BILLS,
              MANAGEMENTS_STATE_PENDING
            )
          );
          setManagementRequestInfo({
            registrarionDate: formatDate(new Date()),
            refferenceNumber: IdCallCenter.toString(),
          });
          setIsClaimLoading(false);
        })
        .catch((error) => {
          setRequestState(MANAGEMENTS_STATE_ERROR);
          setRequestStateData(
            getManagementsRequestTypeData(
              SERVICES_SUBTYPE_OPTIMIZE_BILLS,
              MANAGEMENTS_STATE_ERROR
            )
          );
          setIsClaimLoading(false);
        });
    }
  };

  const StyledDatePicker = styled(DatePicker)(({ theme }) => ({
    padding: "0.5rem",
    backgroundColor: theme.palette.grey[100],
    borderRadius: "4px",
    border: "1px dashed",
    borderColor: theme.palette.grey[300],
    width: "fit-content",
  }));

  return (
    <Box
      component="section"
      className="last-managements"
      paddingBottom={{ md: "5rem" }}
    >
      <ServicesHeader
        title={"Simulador de facturas"}
        step={step}
        showStepper={false}
      />

      {step === 0 && (
        <Grid container justifyContent="center">
          <Grid item md={8} lg={7} flex={1}>
            <Stack
              bgcolor={{ md: "#fff" }}
              borderRadius="12px"
              boxShadow={{ md: "0px 3px 5px 0px rgba(55, 70, 73, 0.20)" }}
              padding="3rem"
              marginY="2.5rem"
              gap={{ xs: "2rem", md: "2.5rem" }}
            >
              <Stack gap="1rem">
                <Typography component="h3" variant="h3">
                  {t("Servicios.Seleccione el punto de suministro")}
                </Typography>
                <Typography
                  component="p"
                  variant="body1"
                  color={theme.palette.grey[700]}
                >
                  {t(
                    "Servicios.Seleccione el suministro sobre el cual desea simular su factura"
                  )}
                  :
                </Typography>

                <Stack width={{ xs: "100%", md: "50%" }}>
                  <ConsumptionsManagementsDeliveryAddressComponent
                    ownerDataCups={ownerData?.cups || []}
                    ownerDataCupSelected={selectedDeliveryAddress}
                    setOwnerDataCupSelected={(
                      ownerDataCupSelected: OwnerDataCups
                    ) => setSelectedDeliveryAddress(ownerDataCupSelected)}
                  />
                </Stack>
              </Stack>
              <Stack gap="1rem">
                <Typography component="h3" variant="h3">
                  {t("Servicios.Mes")}
                </Typography>
                <Typography
                  component="p"
                  variant="body1"
                  color={theme.palette.grey[700]}
                >
                  {t(
                    "Servicios.Normalmente se emiten las facturas dentro de los primeros 15 días de cada mes. La simulación es una previsión el importe que se basa en consumos de meses anteriores. El importe final deberá revisarse en la factura emitida."
                  )}
                </Typography>
                <Stack
                  direction={{ xs: "column", md: "row" }}
                  alignItems={{ xs: "", md: "center" }}
                  gap={"0.5rem"}
                >
                  <Typography>
                    {t("Servicios.La próxima factura a emitirse")}:
                  </Typography>
                  {/* <Typography
                    padding={"0.5rem"}
                    bgcolor={theme.palette.grey[100]}
                    borderRadius={"4px"}
                    border={"1px dashed"}
                    borderColor={theme.palette.grey[300]}
                    width={"fit-content"}
                  >
                    Octubre 2023
                  </Typography> */}
                  <LocalizationProvider
                    dateAdapter={AdapterDayjs}
                    adapterLocale="es"
                  >
                    <StyledDatePicker
                      disablePast
                      views={["month", "year"]}
                      openTo="month"
                      value={dateNextInvoice}
                      onChange={setDateNextInvoice}
                      slotProps={{
                        textField: {
                          InputProps: {
                            placeholder: t("Seleccionar"),
                            sx: {
                              backgroundColor: theme.palette.grey[100],
                              borderRadius: "4px",
                              paddingRight: 0,
                              borderColor: theme.palette.grey[300],
                              "& fieldset": {
                                borderColor: "transparent !important",
                              },
                              "& input": {
                                padding: "0 !important",
                                width: "150px",
                              },
                              "&.Mui-focused fieldset": {
                                borderColor: "red", // Cambia el color del borde al estar enfocado
                              },
                            },
                          },
                        },
                      }}
                    />
                  </LocalizationProvider>
                </Stack>
              </Stack>
              <Box
                component="div"
                display={"flex"}
                alignItems={"flex-start"}
                gap={"0.5rem"}
              >
                <Checkbox
                  sx={{ fontSize: "24px" }}
                  onChange={(event) => {
                    setIsConsentAccepted(event.target.checked);
                  }}
                  onClick={(event) => {
                    event.stopPropagation();
                  }}
                  icon={<span className="t-icon-checkbox-default"></span>}
                  checkedIcon={
                    <span className="t-icon t-icon-checkbox-checked"></span>
                  }
                />
                <Typography component="p" variant="body1">
                  {t(
                    "Servicios.Doy mi consentimiento de que Total Energies pueda utilizar datos de mis consumos anteriores para generar una simulación de facturación."
                  )}
                </Typography>
              </Box>
              <FormControl
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", md: "row" },
                  justifyContent: "space-between",
                  gap: "1rem",
                }}
              >
                <ButtonElement
                  onClick={() => handleSendRequest()}
                  disabled={isButtonDisabled}
                >
                  {t("Servicios.Solicitar simulación")}
                </ButtonElement>
              </FormControl>
            </Stack>
          </Grid>
        </Grid>
      )}
      {[1, 2].includes(step) && (
        <ManagementsRequestState
          state={requestState}
          isLoading={isClaimLoading}
          managementRequestInfo={managementRequestInfo}
          showManagementRequestInfo
          title={requestStateData.title}
          description={requestStateData.description}
          descriptionExtra={requestStateData.descriptionExtra}
        />
      )}
    </Box>
  );
};

export default ServicesInvoiceSimulator;
